"use client";

import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";
import React from "react";

interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
}

interface ErrorBoundaryProps {
	children: React.ReactNode;
	fallback?: React.ComponentType<{ children?: React.ReactNode }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		const isChunkLoadError = error.name === "ChunkLoadError" || error.message?.includes("Loading chunk");

		if (isChunkLoadError) {
			// eslint-disable-next-line no-console
			console.error(`ChunkLoadError caught by boundary: ${error.message}`);
			return;
		}

		logger.error(`Error caught by boundary: ${error.message}`);

		// Serialize error details to avoid circular reference issues
		const serializedError = {
			message: error.message,
			name: error.name,
			stack: error.stack,
			componentStack: errorInfo.componentStack,
			// Include any custom properties on the error
			...(error.cause ? { cause: String(error.cause) } : {}),
		};

		logger.error("Error details:", serializedError);

		// Log specific removeChild errors
		if (error.message.includes("removeChild") || error.message.includes("not a child")) {
			logger.error(`DOM manipulation error detected: ${error.stack}`);
		}

		// Log empty error warning
		if (!error.message && !error.stack) {
			logger.error("Warning: Error object has no message or stack trace", {
				errorType: error.constructor.name,
				errorKeys: Object.keys(error),
			});
		}
	}

	render() {
		if (this.state.hasError) {
			const Fallback = this.props.fallback;
			const errorDetails = (
				<pre
					className={cn(
						process.env.NODE_ENV === "development" ? "opacity-1" : "opacity-0",
						"text-xs whitespace-pre-wrap absolute bottom-0 left-0",
					)}
				>
					{JSON.stringify(
						{
							message: this.state.error?.message,
							stack: this.state.error?.stack,
							name: this.state.error?.name,
						},
						null,
						2,
					)}
				</pre>
			);

			if (Fallback && this.state.error) {
				return <Fallback>{errorDetails}</Fallback>;
			}

			return (
				<div className="p-4 border border-red-200 rounded bg-red-50 relative">
					<h2 className="text-red-800 font-semibold">Something went wrong</h2>
					<p className="text-red-600 text-sm mt-1">{this.state.error?.message || "An unexpected error occurred"}</p>
					{errorDetails}
				</div>
			);
		}

		return this.props.children;
	}
}
